import {
  Flex,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
} from '@bitrise/bitkit';
import { Crash, TypeCrashSortKey } from '@trace/types';
import { DateTime } from 'luxon';
import React, { FunctionComponent, useContext } from 'react';
import { useParams } from 'react-router';

import dataAtIds from '../../../dataAtIds.json';
import { formatter1 } from '../../../utils/metric/getFormattedValue';
import { URLStateContext } from '../../URLState/URLState';
import CrashStatusIcon from './CrashStatusIcon';
import { trackEvent } from '../../../utils/tracking';

interface Props {
  crashes: Crash[];
}

const CrashesTable: FunctionComponent<Props> = ({ crashes }) => {
  const { push, crashSortKey, crashSortDirection, updateUrlState } = useContext(URLStateContext);
  const { appId } = useParams<{ appId: string }>();

  const handleUpdateSort = (key: TypeCrashSortKey) => () => {
    trackEvent('IssuesList.Sort', {
      direction: crashSortDirection,
      field: key,
    });

    updateUrlState({
      crashSortKey: key,
      crashSortDirection: key !== crashSortKey || crashSortDirection === 'asc' ? 'desc' : 'asc',
    });
  };

  return (
    <Table>
      <TableHeader>
        <TableHeaderRow>
          <TableHeaderCell />
          <TableHeaderCell
            breakOn="none"
            data-trace-at-id={dataAtIds.CrashListPageTableHeaderCrashes}
            data-trace-at-sort={crashSortKey === 'occurrenceCount' && crashSortDirection}
            onClick={handleUpdateSort('occurrenceCount')}
            sortable="desc"
            sorted={crashSortKey === 'occurrenceCount' ? crashSortDirection : undefined}
          >
            Crashes
          </TableHeaderCell>
          <TableHeaderCell
            breakOn="none"
            data-trace-at-id={dataAtIds.CrashListPageTableHeaderUsers}
            data-trace-at-sort={crashSortKey === 'deviceCount' && crashSortDirection}
            onClick={handleUpdateSort('deviceCount')}
            sortable="desc"
            sorted={crashSortKey === 'deviceCount' ? crashSortDirection : undefined}
          >
            Users
          </TableHeaderCell>
          <TableHeaderCell
            breakOn="none"
            data-trace-at-id={dataAtIds.CrashListPageTableHeaderLastSeen}
            data-trace-at-sort={crashSortKey === 'lastSeen' && crashSortDirection}
            onClick={handleUpdateSort('lastSeen')}
            sortable="desc"
            sorted={crashSortKey === 'lastSeen' ? crashSortDirection : undefined}
          >
            Last Seen
          </TableHeaderCell>
          <TableHeaderCell>Status</TableHeaderCell>
        </TableHeaderRow>
      </TableHeader>

      <TableBody>
        {crashes.map((crash) => (
          <TableRow
            data-trace-at-id={dataAtIds.CrashListPageTableRow}
            key={crash.id}
            onClick={() => {
              push(`/${appId}/crashes/${crash.id}/event`);
            }}
          >
            <TableCell>
              <Text textColor="grape-5" weight="bold">
                {crash.title}
              </Text>
              {!!crash.description && (
                <Text size="x2" textColor="gray-6">
                  {crash.description}
                </Text>
              )}

              <Flex direction="horizontal" gap="x2" margin="x3" wrap>
                <Flex>
                  <Text config="8" weight="bold">
                    Versions:
                  </Text>
                </Flex>

                {crash.appVersions?.map((version) => (
                  <Flex
                    backgroundColor="gray-2"
                    borderRadius="x1"
                    paddingHorizontal="x2"
                    key={version}
                  >
                    <Text config="8">{version}</Text>
                  </Flex>
                )) || 'N/A'}
              </Flex>
            </TableCell>

            <TableCell shrink>{formatter1(crash.occurrenceCount)}</TableCell>

            <TableCell shrink>{formatter1(crash.deviceCount)}</TableCell>

            <TableCell shrink>{DateTime.fromISO(crash.lastSeen).toRelative()}</TableCell>

            <TableCell shrink>
              <CrashStatusIcon status={crash.status} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default CrashesTable;
